'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface SignupFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [emailSent, setEmailSent] = useState<string | null>(null);
  const { signUp, signInWithGoogle } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<SignupFormData>();

  const passwordValue = watch('password', '');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      const result = await signUp(data.email, data.password, {
        username: data.username,
        displayName: data.username,
      });

      // If session is null, Supabase requires email confirmation
      if (!result?.session) {
        setEmailSent(data.email);
        setIsLoading(false);
        return;
      }

      // Session exists = auto-confirmed (email confirmation disabled in Supabase)
      toast.success('Conta criada! 75 créditos gratuitos adicionados. Bem-vindo ao Core Forges!');
      router.push('/dashboard');
      router.refresh();
    } catch (err: any) {
      setIsLoading(false);
      const msg = err?.message || '';
      if (msg.includes('already registered') || msg.includes('already been registered') || msg.includes('User already registered')) {
        setError('email', { message: 'Este email já está registado. Tenta fazer login.' });
      } else if (msg.includes('Username')) {
        setError('username', { message: 'Este nome de utilizador já existe' });
      } else {
        setError('email', { message: msg || 'Erro ao criar conta. Tenta novamente.' });
      }
    }
  };

  const handleOAuthSignup = async () => {
    setOauthLoading(true);
    try {
      await signInWithGoogle();
      // Redirect happens automatically
    } catch (err: any) {
      setOauthLoading(false);
      toast.error(err?.message || 'Erro ao registar com Google');
    }
  };

  const getPasswordStrength = (pwd: string): { label: string; color: string; width: string } => {
    if (!pwd) return { label: '', color: 'bg-muted', width: '0%' };
    if (pwd.length < 6) return { label: 'Fraca', color: 'bg-danger', width: '25%' };
    if (pwd.length < 10) return { label: 'Razoável', color: 'bg-warning', width: '50%' };
    if (pwd.length < 14) return { label: 'Boa', color: 'bg-success', width: '75%' };
    return { label: 'Excelente', color: 'bg-accent', width: '100%' };
  };

  const strength = getPasswordStrength(passwordValue);

  // Email confirmation pending state
  if (emailSent) {
    return (
      <div className="animate-fade-in text-center py-8">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <Icon name="EnvelopeIcon" size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Confirma o teu email</h2>
        <p className="text-sm text-muted-foreground mb-1">
          Enviámos um link de confirmação para:
        </p>
        <p className="text-sm font-semibold text-foreground mb-6">{emailSent}</p>
        <p className="text-xs text-muted-foreground mb-8 max-w-xs mx-auto">
          Clica no link no email para ativar a tua conta. Depois podes fazer login normalmente.
        </p>
        <div className="space-y-3">
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 active:scale-95 transition-all duration-150"
          >
            Ir para o Login
          </button>
          <button
            type="button"
            onClick={() => setEmailSent(null)}
            className="w-full py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Voltar ao registo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1">Cria a tua conta</h2>
        <div className="flex items-center gap-2">
          <p className="text-sm text-muted-foreground">Começa com</p>
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-success/15 text-success">
            <Icon name="BoltIcon" size={10} variant="solid" />
            75 créditos gratuitos/dia
          </span>
        </div>
      </div>

      {/* Social auth */}
      <div className="mb-6">
        <button
          type="button"
          onClick={handleOAuthSignup}
          disabled={oauthLoading || isLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-muted/50 border border-border hover:bg-muted transition-all duration-150 text-sm font-medium text-foreground active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {oauthLoading ? (
            <Icon name="ArrowPathIcon" size={16} className="animate-spin text-muted-foreground" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          )}
          Registar com Google
        </button>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">ou com email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Username */}
        <div>
          <label htmlFor="signup-username" className="block text-sm font-medium text-foreground mb-1.5">
            Nome de utilizador
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Será o teu perfil público: /profile/username</p>
          <input
            id="signup-username"
            type="text"
            autoComplete="username"
            placeholder="miguel_forge"
            className={`
              w-full px-4 py-2.5 rounded-xl bg-input border text-sm text-foreground placeholder:text-muted-foreground
              focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-150
              ${errors.username ? 'border-danger' : 'border-border'}
            `}
            {...register('username', {
              required: 'O nome de utilizador é obrigatório',
              minLength: { value: 3, message: 'Mínimo 3 caracteres' },
              maxLength: { value: 24, message: 'Máximo 24 caracteres' },
              pattern: { value: /^[a-zA-Z0-9_]+$/, message: 'Apenas letras, números e _' },
            })}
          />
          {errors.username && <p className="mt-1.5 text-xs text-danger">{errors.username.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="signup-email" className="block text-sm font-medium text-foreground mb-1.5">
            Email
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="miguel@exemplo.com"
            className={`
              w-full px-4 py-2.5 rounded-xl bg-input border text-sm text-foreground placeholder:text-muted-foreground
              focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-150
              ${errors.email ? 'border-danger' : 'border-border'}
            `}
            {...register('email', {
              required: 'O email é obrigatório',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
            })}
          />
          {errors.email && <p className="mt-1.5 text-xs text-danger">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="block text-sm font-medium text-foreground mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
              className={`
                w-full px-4 py-2.5 pr-11 rounded-xl bg-input border text-sm text-foreground placeholder:text-muted-foreground
                focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-150
                ${errors.password ? 'border-danger' : 'border-border'}
              `}
              {...register('password', {
                required: 'A password é obrigatória',
                minLength: { value: 8, message: 'Mínimo 8 caracteres' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {passwordValue && (
            <div className="mt-2">
              <div className="h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                  style={{ width: strength.width }}
                />
              </div>
              {strength.label && (
                <p className="text-xs text-muted-foreground mt-1">Força: {strength.label}</p>
              )}
            </div>
          )}
          {errors.password && <p className="mt-1.5 text-xs text-danger">{errors.password.message}</p>}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="signup-confirm" className="block text-sm font-medium text-foreground mb-1.5">
            Confirmar password
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repete a password"
              className={`
                w-full px-4 py-2.5 pr-11 rounded-xl bg-input border text-sm text-foreground placeholder:text-muted-foreground
                focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-150
                ${errors.confirmPassword ? 'border-danger' : 'border-border'}
              `}
              {...register('confirmPassword', {
                required: 'Confirma a password',
                validate: (val) => val === passwordValue || 'As passwords não coincidem',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1.5 text-xs text-danger">{errors.confirmPassword.message}</p>}
        </div>

        {/* Terms */}
        <div>
          <div className="flex items-start gap-2">
            <input
              id="signup-terms"
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded border-border bg-input text-primary focus:ring-primary/50 cursor-pointer flex-shrink-0"
              {...register('terms', { required: 'Tens de aceitar os termos' })}
            />
            <label htmlFor="signup-terms" className="text-xs text-muted-foreground cursor-pointer leading-relaxed">
              Aceito os{' '}
              <span className="text-primary hover:text-primary/80 transition-colors">Termos de Serviço</span>
              {' '}e a{' '}
              <span className="text-primary hover:text-primary/80 transition-colors">Política de Privacidade</span>
            </label>
          </div>
          {errors.terms && <p className="mt-1.5 text-xs text-danger">{errors.terms.message}</p>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading || oauthLoading}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          style={{ minHeight: '44px' }}
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              A criar conta...
            </>
          ) : (
            'Criar conta gratuita'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Já tens conta?{' '}
        <button onClick={onSwitchToLogin} className="text-primary hover:text-primary/80 font-medium transition-colors">
          Faz login aqui
        </button>
      </p>
    </div>
  );
}